import { useCallback, useEffect, useRef, useState } from 'react'

import { useAppDispatch, useAppSelector } from 'reduxTyped'
import { prepareSimulatorData } from 'apps/flowPicker/helpers/prepareSimulatorData'
import { FLOW_PICKER_ANALYTICS_EVENT } from 'apps/flowPicker/lib/constants'
import { getCurrentAccount } from 'common/core/selectors/appSelectors'
import { fetchFlowContentData } from 'common/flow/actions/flowActions'
import { IFullFlow } from 'common/flow/flowInterfaces'
import { getById } from 'common/flow/selectors/flowSelectors'
import { handleError } from 'shared/api/lib/errors/handlers'
import { FsFlow } from 'shared/api/requests/cms/schemas'
import { analyticsService } from 'utils/services/analytics'

interface UseFlowPickerPreviewParams {
  lastSelectedFlowId: string
  isValid: boolean
  cmsFlow: FsFlow
}

export const useFlowPickerPreview = ({
  lastSelectedFlowId,
  isValid,
  cmsFlow,
}: UseFlowPickerPreviewParams) => {
  const dispatch = useAppDispatch()
  const phoneContainerRef = useRef<HTMLDivElement>(null)
  const simulatorRef = useRef<ReturnType<typeof window.MC_FLOWSIMULATOR.createSimulator>>(undefined)
  const [isSimulatorThrownError, setIsSimulatorThrownError] = useState(false)
  const [isFlowContentDataFetching, setIsFlowContentDataFetching] = useState(false)
  const selectedFlowName = cmsFlow?.name
  const selectedFlowPath = cmsFlow?.path
  const hasPublishedContent = Boolean(cmsFlow?.has_published_content)

  const flow = useAppSelector((state) => getById(state, lastSelectedFlowId)) as IFullFlow
  const isFetching = isFlowContentDataFetching || flow?.isFetching
  const isFlowDataAvailable = Boolean(
    !isFetching && flow?.contents?.length && flow?.root_content_id,
  )

  const { title: name, ava_big: avatarUrl } = useAppSelector(getCurrentAccount)

  const handleRestartSimulator = useCallback(() => {
    if (!simulatorRef.current) return
    simulatorRef.current.restart()
    analyticsService.sendEvent(`${FLOW_PICKER_ANALYTICS_EVENT}.FLOW_SIMULATOR.RESTART`)
  }, [])

  const resetSimulator = useCallback(() => {
    if (simulatorRef.current && phoneContainerRef.current) {
      simulatorRef.current = undefined
      phoneContainerRef.current.querySelector('.flow-simulator')?.remove()
    }
  }, [])

  useEffect(() => {
    const fetchFlowContentDataAction = async () => {
      if (
        lastSelectedFlowId &&
        !isFlowDataAvailable &&
        hasPublishedContent &&
        isValid &&
        selectedFlowName &&
        selectedFlowPath
      ) {
        setIsFlowContentDataFetching(true)
        await dispatch(
          fetchFlowContentData({
            flowId: lastSelectedFlowId,
            flowName: selectedFlowName,
            flowPath: selectedFlowPath,
          }),
        )

        setIsFlowContentDataFetching(false)
      }
    }

    fetchFlowContentDataAction()
  }, [
    lastSelectedFlowId,
    dispatch,
    isFlowDataAvailable,
    hasPublishedContent,
    isValid,
    selectedFlowName,
    selectedFlowPath,
  ])

  useEffect(() => {
    const phoneContainer = phoneContainerRef.current
    if (!phoneContainer || !isFlowDataAvailable || isFetching) return

    try {
      simulatorRef.current = window.MC_FLOWSIMULATOR.createSimulator(
        phoneContainer,
        prepareSimulatorData(flow),
        { avatarUrl, name, simpleMode: true },
      )
      analyticsService.sendEvent(`${FLOW_PICKER_ANALYTICS_EVENT}.FLOW_SIMULATOR.SHOW`)
    } catch (error) {
      resetSimulator()
      setIsSimulatorThrownError(true)
      handleError(error)
    }

    return () => {
      resetSimulator()
    }
  }, [isFlowDataAvailable, isFetching, flow, avatarUrl, name, resetSimulator, dispatch])

  return {
    phoneContainerRef,
    isFetching,
    handleRestartSimulator,
    isFlowDataAvailable,
    lastSelectedFlowId,
    isSimulatorThrownError,
  }
}
